import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { CalendarClock, Truck, Plus, X, Package, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  scheduled: { label: 'Programada', className: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  completed: { label: 'Completada', className: 'bg-green-500/15 text-green-300 border-green-500/30' },
  cancelled: { label: 'Cancelada',  className: 'bg-red-500/15 text-red-300 border-red-500/30' },
};

const CARRIERS = [
  { value: 'estafeta', label: 'Estafeta' },
  { value: 'dhl', label: 'DHL' },
  { value: 'ampm', label: 'amPm' },
  { value: 'paquetexpress', label: 'Paquetexpress' },
  { value: 'ivoy', label: 'iVoy' },
];

function tomorrowYMD() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function todayYMD() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function startOfWeekYMD() {
  const d = new Date();
  const day = d.getDay(); // 0=Sun
  const diff = (day === 0 ? -6 : 1 - day); // Monday start
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
}
function endOfWeekYMD() {
  const d = new Date();
  const day = d.getDay();
  const diff = (day === 0 ? 0 : 7 - day);
  d.setDate(d.getDate() + diff);
  d.setHours(23, 59, 59, 999);
  return d.toISOString().slice(0, 10);
}

export default function Pickups() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [pickups, setPickups] = useState([]);
  const [eligibleOrders, setEligibleOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);
  const [form, setForm] = useState({
    carrier: 'ampm',
    pickup_date: tomorrowYMD(),
    pickup_time_from: '09:00',
    pickup_time_to: '18:00',
    order_ids: [],
    notes: '',
  });

  const fetchPickups = async () => {
    try {
      const r = await axios.get(`${API}/envia/admin/pickups`, { headers });
      setPickups(Array.isArray(r.data) ? r.data : []);
    } catch {
      toast.error('Error cargando recolecciones');
    } finally {
      setLoading(false);
    }
  };

  const fetchEligibleOrders = async () => {
    try {
      const r = await axios.get(`${API}/orders/?per_page=200`, { headers });
      const rows = r.data?.orders || [];
      const eligible = rows.filter(o => o.envia_tracking_number && !o.pickup_scheduled);
      setEligibleOrders(eligible);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    fetchPickups();
    fetchEligibleOrders();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stats
  const stats = useMemo(() => {
    const today = todayYMD();
    const sow = startOfWeekYMD();
    const eow = endOfWeekYMD();
    const scheduled = pickups.filter(p => p.status === 'scheduled');
    return {
      total: scheduled.length,
      today: scheduled.filter(p => p.pickup_date === today).length,
      thisWeek: scheduled.filter(p => p.pickup_date >= sow && p.pickup_date <= eow).length,
    };
  }, [pickups]);

  const handleToggleOrder = (orderId) => {
    setForm(f => ({
      ...f,
      order_ids: f.order_ids.includes(orderId)
        ? f.order_ids.filter(x => x !== orderId)
        : [...f.order_ids, orderId],
    }));
  };

  const openDialog = () => {
    setForm({
      carrier: 'ampm',
      pickup_date: tomorrowYMD(),
      pickup_time_from: '09:00',
      pickup_time_to: '18:00',
      order_ids: [],
      notes: '',
    });
    fetchEligibleOrders();
    setDialogOpen(true);
  };

  const handleSchedule = async () => {
    if (form.order_ids.length === 0) {
      toast.error('Selecciona al menos una orden');
      return;
    }
    if (form.pickup_time_from >= form.pickup_time_to) {
      toast.error('La hora de inicio debe ser menor que la de fin');
      return;
    }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/envia/pickup/schedule`, form, { headers });
      setPickups(p => [r.data, ...p]);
      if (r.data?.envia_status && r.data.envia_status !== 'ok') {
        toast.warning(`Programada localmente. Envia.com respondió: ${r.data?.envia_raw?.error?.message || r.data.envia_status}`);
      } else {
        toast.success('Recolección programada con Envia.com');
      }
      setDialogOpen(false);
      fetchEligibleOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error programando recolección');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async (pickup) => {
    if (!window.confirm(`¿Cancelar recolección del ${pickup.pickup_date} con ${pickup.carrier?.toUpperCase()}?`)) return;
    setCancellingId(pickup.id);
    try {
      const r = await axios.delete(`${API}/envia/admin/pickups/${pickup.id}`, { headers });
      setPickups(prev => prev.map(p => p.id === pickup.id ? r.data : p));
      toast.success('Recolección cancelada');
      fetchEligibleOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error cancelando');
    } finally {
      setCancellingId(null);
    }
  };

  const isFuture = (date) => date >= todayYMD();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-display font-semibold flex items-center gap-2">
            <CalendarClock className="h-5 w-5" /> Recolecciones
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Programa recolecciones con las paqueterías directamente desde aquí</p>
        </div>
        <Button onClick={openDialog} className="gap-2" data-testid="new-pickup-button">
          <Plus className="h-4 w-4" /> Nueva Recolección
        </Button>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-blue-500/30 bg-blue-500/5" data-testid="pickups-stat-total">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="p-3 rounded-md bg-blue-500/15"><CalendarClock className="h-5 w-5 text-blue-300" /></div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Total Programadas</p>
              <p className="text-3xl font-display font-bold text-blue-300 tabular-nums">{stats.total}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-amber-500/30 bg-amber-500/5" data-testid="pickups-stat-today">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="p-3 rounded-md bg-amber-500/15"><Truck className="h-5 w-5 text-amber-300" /></div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Hoy</p>
              <p className="text-3xl font-display font-bold text-amber-300 tabular-nums">{stats.today}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-green-500/30 bg-green-500/5" data-testid="pickups-stat-week">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="p-3 rounded-md bg-green-500/15"><CheckCircle2 className="h-5 w-5 text-green-300" /></div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Esta semana</p>
              <p className="text-3xl font-display font-bold text-green-300 tabular-nums">{stats.thisWeek}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      {loading ? (
        <Card><CardContent className="p-8 text-center text-muted-foreground">Cargando...</CardContent></Card>
      ) : pickups.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <CalendarClock className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No hay recolecciones programadas</p>
            <p className="text-xs text-muted-foreground mt-1">Presiona "Nueva Recolección" para programar una.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table data-testid="pickups-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha y horario</TableHead>
                  <TableHead>Paquetería</TableHead>
                  <TableHead className="text-center">Paquetes</TableHead>
                  <TableHead>Órdenes</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pickups.map(p => {
                  const meta = STATUS_META[p.status] || STATUS_META.scheduled;
                  const canCancel = p.status === 'scheduled' && isFuture(p.pickup_date);
                  return (
                    <TableRow key={p.id} data-testid={`pickup-row-${p.id}`}>
                      <TableCell>
                        <div className="text-sm font-medium">{p.pickup_date}</div>
                        <div className="text-xs text-muted-foreground">{p.pickup_time_from} – {p.pickup_time_to}</div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 rounded-md bg-secondary"><Truck className="h-3.5 w-3.5" /></div>
                          <span className="text-sm font-medium">{p.carrier?.toUpperCase()}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="secondary" className="tabular-nums">{p.order_count ?? (p.order_ids?.length || 0)}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1 max-w-xs">
                          {(p.order_numbers || []).slice(0, 4).map(num => (
                            <Link key={num} to={`/admin/ordenes`} className="inline-flex">
                              <Badge variant="outline" className="text-[10px] font-mono hover:bg-secondary/40 cursor-pointer">{num}</Badge>
                            </Link>
                          ))}
                          {(p.order_numbers?.length || 0) > 4 && (
                            <Badge variant="outline" className="text-[10px]">+{p.order_numbers.length - 4}</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={meta.className} data-testid={`pickup-status-${p.id}`}>
                          {meta.label}
                        </Badge>
                        {p.envia_status && p.envia_status !== 'ok' && (
                          <div className="mt-1 flex items-center gap-1 text-[10px] text-amber-300/80">
                            <AlertTriangle className="h-3 w-3" /> Envia: {p.envia_status}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {canCancel ? (
                          <Button variant="ghost" size="sm" onClick={() => handleCancel(p)} disabled={cancellingId === p.id} className="text-red-400 hover:text-red-300 hover:bg-red-500/10 gap-1.5" data-testid={`cancel-pickup-${p.id}`}>
                            <X className="h-3.5 w-3.5" />
                            {cancellingId === p.id ? 'Cancelando...' : 'Cancelar'}
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* New pickup dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="new-pickup-dialog">
          <DialogHeader>
            <DialogTitle>Nueva Recolección</DialogTitle>
            <DialogDescription>
              Programa una recolección con la paquetería para los pedidos seleccionados
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="pickup-carrier">Paquetería *</Label>
                <Select value={form.carrier} onValueChange={v => setForm({ ...form, carrier: v })}>
                  <SelectTrigger id="pickup-carrier" data-testid="pickup-carrier-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CARRIERS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pickup-date">Fecha *</Label>
                <Input
                  id="pickup-date"
                  type="date"
                  min={tomorrowYMD()}
                  value={form.pickup_date}
                  onChange={e => setForm({ ...form, pickup_date: e.target.value })}
                  data-testid="pickup-date-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pickup-from">Hora inicio *</Label>
                <Input id="pickup-from" type="time" value={form.pickup_time_from} onChange={e => setForm({ ...form, pickup_time_from: e.target.value })} data-testid="pickup-from-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pickup-to">Hora fin *</Label>
                <Input id="pickup-to" type="time" value={form.pickup_time_to} onChange={e => setForm({ ...form, pickup_time_to: e.target.value })} data-testid="pickup-to-input" />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Órdenes a recolectar *</Label>
                <span className="text-xs text-muted-foreground">{form.order_ids.length} seleccionada(s)</span>
              </div>
              <div className="border rounded-md max-h-60 overflow-y-auto" data-testid="pickup-orders-list">
                {eligibleOrders.length === 0 ? (
                  <div className="p-6 text-center text-sm text-muted-foreground">
                    <Package className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    No hay órdenes pendientes con guía generada que aún no estén programadas para recolección.
                  </div>
                ) : (
                  <ul className="divide-y">
                    {eligibleOrders.map(o => {
                      const checked = form.order_ids.includes(o.id);
                      return (
                        <li key={o.id}>
                          <label className="flex items-center gap-3 px-3 py-2 hover:bg-secondary/30 cursor-pointer">
                            <Checkbox checked={checked} onCheckedChange={() => handleToggleOrder(o.id)} data-testid={`pickup-order-checkbox-${o.order_number}`} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-xs font-medium">{o.order_number}</span>
                                <Badge variant="outline" className="text-[10px]">{o.envia_carrier?.toUpperCase() || '—'}</Badge>
                              </div>
                              <div className="text-xs text-muted-foreground truncate">{o.user_name} · <code className="font-mono">{o.envia_tracking_number}</code></div>
                            </div>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="pickup-notes">Notas (opcional)</Label>
              <textarea
                id="pickup-notes"
                value={form.notes}
                onChange={e => setForm({ ...form, notes: e.target.value })}
                rows={3}
                maxLength={500}
                placeholder="Indicaciones especiales para la recolección..."
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                data-testid="pickup-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancelar</Button>
            <Button onClick={handleSchedule} disabled={saving || form.order_ids.length === 0} data-testid="pickup-schedule-button">
              {saving ? 'Programando...' : 'Programar recolección'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
